// Stack Recipe Funnel — admin-only widget showing CSV-template downloads,
// per-recipe attribution, drip progression, and signup conversion.
//
// Mirrors the CrashCourseFunnelCard pattern. Drops onto AdminFunnel below
// the crash-course funnel.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Download, Layers, TrendingUp, XCircle, CheckCircle2, Calendar, Trophy, Share2, Users } from "lucide-react";

const RECIPE_NICE_NAMES = {
  "probate-tax-delinquent":         "🏆 The Goldmine",
  "vacant-out-of-state-absentee":   "🏚️ The Walkaway",
  "pre-foreclosure-high-equity":    "⏰ The Deadline",
  "tired-landlord":                 "🔨 The Tired Landlord",
  "divorce-equity":                 "💔 The Split",
  "inherited-vacant":               "💎 The Inheritance",
};

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      className="bg-white border border-zinc-200 rounded-lg p-4"
      data-testid={testid || `stack-recipe-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-zinc-500">
        <Icon size={12} style={{ color: accent }} /> {label}
      </div>
      <div className="text-2xl font-bold text-zinc-900 mt-1 tabular-nums">{value}</div>
    </div>
  );
}

export default function StackRecipeFunnelCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stack-recipe-funnel")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    const id = setInterval(() => {
      api.get("/admin/stack-recipe-funnel").then((r) => setData(r.data)).catch(() => {});
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading || !data) {
    return (
      <div className="bg-white border rounded-lg p-6 mt-8" data-testid="stack-recipe-funnel-loading">
        <div className="h-4 w-48 bg-zinc-200 rounded animate-pulse" />
      </div>
    );
  }

  const {
    total_captures,
    captures_last_30d,
    captures_by_recipe,
    captures_by_lesson_day,
    captures_by_source,
    captures_by_channel,
    top_referrers,
    opted_out,
    completed_full_drip,
    signups_by_recipe,
    total_signups_from_drip,
    conversion_rate_pct,
  } = data;

  const topRecipe = captures_by_recipe[0];
  const signupsByRecipeMap = Object.fromEntries(
    (signups_by_recipe || []).map((s) => [s.recipe_slug, s.signups]),
  );

  return (
    <div className="bg-white border rounded-lg p-6 mt-8" data-testid="stack-recipe-funnel-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
          <Layers size={18} className="text-indigo-600" />
          Stack Recipe Library — Conversion Funnel
        </h2>
        <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">Auto-refresh · 60s</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat icon={Download} label="Total captures" value={total_captures} accent="#6366F1" testid="stack-recipe-stat-total" />
        <Stat icon={Calendar} label="Last 30 days" value={captures_last_30d} accent="#0EA5E9" testid="stack-recipe-stat-last-30d" />
        <Stat icon={TrendingUp} label="Signups from drip" value={total_signups_from_drip} accent="#10B981" testid="stack-recipe-stat-signups" />
        <Stat icon={Trophy} label="Conversion rate" value={`${conversion_rate_pct}%`} accent="#F59E0B" testid="stack-recipe-stat-conv" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat icon={CheckCircle2} label="Completed full drip" value={completed_full_drip} accent="#16A34A" />
        <Stat icon={XCircle} label="Opted out" value={opted_out} accent="#DC2626" />
        <Stat
          icon={Layers}
          label="Recipes pulling weight"
          value={(captures_by_recipe || []).length}
          accent="#7C3AED"
        />
        <Stat
          icon={Trophy}
          label="Top recipe"
          value={topRecipe ? (RECIPE_NICE_NAMES[topRecipe.recipe_slug] || topRecipe.recipe_slug).split(" ")[0] : "—"}
          accent="#F59E0B"
        />
      </div>

      {/* Per-recipe breakdown */}
      {captures_by_recipe && captures_by_recipe.length > 0 && (
        <div className="mb-5">
          <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2">By recipe</div>
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="stack-recipe-by-recipe-table">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Recipe</th>
                  <th className="text-right px-4 py-2 font-semibold">Captures</th>
                  <th className="text-right px-4 py-2 font-semibold">Signups</th>
                  <th className="text-right px-4 py-2 font-semibold">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {captures_by_recipe.map((r) => {
                  const signups = signupsByRecipeMap[r.recipe_slug] || 0;
                  const conv = r.captures > 0 ? ((signups / r.captures) * 100).toFixed(1) : "0.0";
                  return (
                    <tr key={r.recipe_slug} className="border-t border-zinc-100" data-testid={`stack-recipe-row-${r.recipe_slug}`}>
                      <td className="px-4 py-2 text-zinc-900">
                        {RECIPE_NICE_NAMES[r.recipe_slug] || r.recipe_slug}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">{r.captures}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{signups}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-zinc-500">{conv}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Drip progression */}
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2">Drip lesson progression</div>
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Lesson</th>
                  <th className="text-right px-4 py-2 font-semibold">Captures</th>
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2, 3, 4, 5].map((day) => {
                  const count = captures_by_lesson_day[day] || 0;
                  const label = day === 0 ? "Welcome (Day 0)" : `Lesson ${day}`;
                  return (
                    <tr key={day} className="border-t border-zinc-100">
                      <td className="px-4 py-2 text-zinc-900">{label}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2">Capture source</div>
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Source</th>
                  <th className="text-right px-4 py-2 font-semibold">Count</th>
                </tr>
              </thead>
              <tbody>
                {(captures_by_source || []).length === 0 ? (
                  <tr><td colSpan={2} className="px-4 py-2 text-zinc-400 italic">No captures yet</td></tr>
                ) : (
                  captures_by_source.map((row) => (
                    <tr key={row.source} className="border-t border-zinc-100">
                      <td className="px-4 py-2 text-zinc-900 capitalize">{row.source}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Share-channel + Top referrers — surfaces viral distribution data. */}
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2 flex items-center gap-1.5">
            <Share2 size={11} /> By share channel
          </div>
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="stack-recipe-channel-table">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Channel</th>
                  <th className="text-right px-4 py-2 font-semibold">Captures</th>
                </tr>
              </thead>
              <tbody>
                {(captures_by_channel || []).length === 0 ? (
                  <tr><td colSpan={2} className="px-4 py-2 text-zinc-400 italic">No share-attributed captures yet</td></tr>
                ) : (
                  captures_by_channel.map((row) => (
                    <tr key={row.channel} className="border-t border-zinc-100" data-testid={`channel-row-${row.channel}`}>
                      <td className="px-4 py-2 text-zinc-900 capitalize">
                        {row.channel === "twitter" ? "X / Twitter" : row.channel}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2 flex items-center gap-1.5">
            <Users size={11} /> Top referrers (your viral champions)
          </div>
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="stack-recipe-referrers-table">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">User</th>
                  <th className="text-right px-4 py-2 font-semibold">Captures driven</th>
                </tr>
              </thead>
              <tbody>
                {(top_referrers || []).length === 0 ? (
                  <tr><td colSpan={2} className="px-4 py-2 text-zinc-400 italic">No user-attributed shares yet</td></tr>
                ) : (
                  top_referrers.map((r) => (
                    <tr key={r.user_id} className="border-t border-zinc-100" data-testid={`referrer-row-${r.user_id}`}>
                      <td className="px-4 py-2 text-zinc-900">
                        <div className="font-semibold">{r.name || r.email.split("@")[0]}</div>
                        <div className="text-[10px] text-zinc-500 truncate">{r.email}</div>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-bold">{r.captures}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
